<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" :columns="false" @copy="copyTable">
      <div v-if="shifts.length" class="rd-chips" role="group" :aria-label="$t('shifts')">
        <button
          v-for="shift in shifts"
          :key="shift"
          type="button"
          class="rd-chip"
          :aria-pressed="String(selectedShift === shift)"
          @click="selectedShift = shift"
        >
          <report-shift-tag :shift="Number(shift)" long></report-shift-tag>
        </button>
      </div>
      <div class="rd-seg" role="tablist" :aria-label="$t('view')">
        <button
          v-for="layout in layoutTabs"
          :key="layout.value"
          type="button"
          role="tab"
          :aria-selected="String(selectedLayout === layout.value)"
          @click="selectedLayout = layout.value"
        >
          {{ layout.text }}
        </button>
      </div>
      <report-checklist-menu
        v-model="visibleValues"
        :label="$t('visible_columns')"
        :items="columnItems"
      ></report-checklist-menu>
    </report-tools>

    <report-state v-if="isRange" icon="mdi-calendar-today" :title="$t('daily_report')"></report-state>
    <report-state
      v-else-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card gm-scroll">
      <table class="rd-tbl gm-orderers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th v-for="header in shownHeaders" :key="header.value" scope="col">
              {{ header.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <template v-for="header in shownHeaders">
              <td v-if="header.value === 'fullname'" :key="header.value" class="is-name" :title="`ID ${row.userId}`">
                {{ row.fullname }}
              </td>
              <td v-else-if="header.value === 'date'" :key="header.value" class="rd-num rd-muted">{{ row.dateText }}</td>
              <td v-else-if="header.value === 'total'" :key="header.value" class="gm-orderers__note">{{ row.note }}</td>
              <td v-else :key="header.value" class="gm-orderers__cell">
                <span v-for="(meal, mealIndex) in row.cells[header.value]" :key="mealIndex" class="gm-orderers__line">
                  <b v-if="selectedLayout !== 1" class="rd-num">{{ meal.quantity }}<template v-if="selectedLayout === 0">×</template></b>
                  <span v-if="selectedLayout !== 2">{{ meal.name }}</span>
                </span>
                <span v-if="!row.cells[header.value] || !row.cells[header.value].length" class="rd-dash" aria-hidden="true">–</span>
              </td>
            </template>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <template v-for="header in shownHeaders">
              <td v-if="header.value === 'fullname'" :key="header.value">{{ $t("total") }}</td>
              <td v-else-if="categoryTotals[header.value] !== undefined" :key="header.value" class="rd-num">
                {{ categoryTotals[header.value] }}
              </td>
              <td v-else :key="header.value"></td>
            </template>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import ReportTabGroupMealsTabCustomers2 from "@/components/report/group-meals/ReportTabGroupMealsTabCustomers2.vue";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import hasGroupOrdererRows from "../../../shared/group/hasGroupOrdererRows";
import { copyText, rowsToTsv } from "../../copyTable";
import ReportChecklistMenu from "../../ReportChecklistMenu.vue";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Naručioci (jela iz grupa) na računaru: tabela radnika sa obrocima po grupama jela za jednu firmu i jednu smenu
 * (oznake smena u alatima), prikaz (količine i nazivi / nazivi / količine) i izbor kolona u alatima. Radi samo
 * za jedan dan. Firmu i pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz starog taba
 * (štampa i Excel prate izabrane kolone).
 */
export default {
  name: "ReportDesktopGroupOrderers",
  extends: ReportTabGroupMealsTabCustomers2,
  mixins: [hasGroupOrdererRows],
  components: { ReportChecklistMenu, ReportShiftTag, ReportState, ReportTools },
  computed: {
    layoutTabs() {
      // četvrti izbor starog prikaza ("Rezervni obroci") nikad nije imao prikaz, pa ga ovde nema
      return this.layouts.filter((layout) => layout.value < 3);
    },
    /** Kolone koje se mogu birati: sve osim rednog broja (on je uvek tu). */
    columnItems() {
      return this.headers.filter((header) => header.value !== "num").map((header) => ({ value: header.value, text: header.text }));
    },
    visibleValues: {
      get() {
        return this.visibleHeaders.map((header) => header.value);
      },
      set(values) {
        this.visibleHeaders = this.headers.filter((header) => header.value === "num" || values.includes(header.value));
      },
    },
    shownHeaders() {
      return this.sortedVisibleHeaders.filter((header) => header.value !== "num");
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_orders_found");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.rows.length, tone: "inverse" },
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: this.mealTotal },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = ["#", ...this.shownHeaders.map((header) => header.text)];
      const lines = (meals) =>
        meals
          .map((meal) => (this.selectedLayout === 2 ? meal.quantity : this.selectedLayout === 1 ? meal.name : `${meal.quantity}× ${meal.name}`))
          .join("; ");
      const rows = this.rows.map((row, index) => [
        index + 1,
        ...this.shownHeaders.map((header) => {
          if (header.value === "fullname") return row.fullname;
          if (header.value === "date") return row.dateText;
          if (header.value === "total") return row.note;
          return lines(row.cells[header.value] || []);
        }),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
